import { useState } from "react";
import { Link } from "react-router-dom";
import type { MediaItem } from "@/types/media";
import { categoryName } from "@/lib/media";
import { useFavorites } from "@/hooks/useFavorites";

const genreLinkMap: Record<string, string> = {
  科幻: "sci-fi",
  悬疑: "suspense",
  剧情: "drama",
  奇幻: "fantasy",
};

function genreLink(genre: string): string {
  return genreLinkMap[genre] ?? "movie";
}

export default function DetailHero({ item }: { item: MediaItem }) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");
  const favorited = isFavorite(item.id);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2000);
  };

  const handleFavorite = () => {
    const now = toggleFavorite(item.id);
    showToast(now ? "已加入收藏" : "已取消收藏");
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}${__BASE_PATH__}detail/${item.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: item.title, url: shareUrl });
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      showToast("链接已复制，快分享给朋友吧");
    } catch {
      showToast("分享链接已生成，可手动复制地址栏链接");
    }
  };

  const infoRows = [
    { label: "导演", value: item.director },
    { label: "主演", value: item.cast.join(" / ") },
    { label: "类型", value: item.genres.join(" / ") },
    { label: "地区", value: item.region },
    { label: "上映年份", value: String(item.year) },
    {
      label: item.category === "movie" ? "片长" : "集数",
      value: item.duration,
    },
  ];

  return (
    <div className="relative w-full overflow-hidden bg-secondary-950">
      <img
        src={item.posterWide}
        alt={`${item.title}剧照`}
        className="absolute inset-0 h-full w-full object-cover object-center opacity-40"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-secondary-950 via-secondary-950/90 to-secondary-950/70"></div>

      <div className="relative mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
        <div className="flex flex-col gap-6 md:flex-row md:gap-10">
          <div className="w-[180px] shrink-0 self-center overflow-hidden rounded-lg md:w-[240px] md:self-start">
            <img
              src={item.poster}
              alt={`${item.title}海报 - 红狐影视`}
              title={`${item.title} + 红狐影视`}
              className="aspect-[2/3] w-full object-cover object-top"
            />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-semibold text-background-50">
                {categoryName(item.category)}
              </span>
              {item.genres.map((g) => (
                <span
                  key={g}
                  className="rounded-md bg-background-50/15 px-2.5 py-1 text-xs text-secondary-100 backdrop-blur-sm"
                >
                  {g}
                </span>
              ))}
            </div>

            <h1 className="mt-4 font-heading text-2xl font-extrabold text-background-50 md:text-4xl">
              {item.title}
            </h1>
            <p className="mt-2 text-sm text-secondary-300">{item.originalTitle}</p>
            <p className="mt-3 text-sm italic text-accent-300">「{item.tagline}」</p>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-secondary-100">
              <span className="flex items-center gap-1.5">
                <i className="ri-star-fill text-accent-400 text-lg"></i>
                <strong className="text-lg text-background-50">
                  {item.rating.toFixed(1)}
                </strong>
                <span className="text-secondary-300">/ 10</span>
              </span>
              <span>{item.year}</span>
              <span>{item.region}</span>
              <span>{item.duration}</span>
            </div>

            <div className="mt-7 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex items-center gap-2 whitespace-nowrap rounded-md px-6 py-3 text-sm font-semibold transition-colors cursor-pointer ${
                  favorited
                    ? "bg-accent-500 text-secondary-950"
                    : "bg-primary-500 text-background-50 hover:bg-primary-600"
                }`}
              >
                <i className={favorited ? "ri-heart-fill" : "ri-heart-line"}></i>
                {favorited ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 bg-background-50/10 px-6 py-3 text-sm font-semibold text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/20 cursor-pointer"
              >
                <i className="ri-share-forward-line"></i>
                分享
              </button>
            </div>
          </div>
        </div>

        <dl className="mt-8 grid grid-cols-1 gap-x-8 gap-y-3 border-t border-secondary-800 pt-6 sm:grid-cols-2">
          {infoRows.map((row) => (
            <div key={row.label} className="flex gap-3 text-sm">
              <dt className="w-16 shrink-0 text-secondary-400">{row.label}</dt>
              <dd className="text-secondary-100">{row.value}</dd>
            </div>
          ))}
        </dl>

        <div className="mt-6 flex flex-wrap items-center gap-3 text-sm">
          <span className="text-secondary-400">所属分类：</span>
          <Link
            to={`/category/${item.category}`}
            className="rounded-md bg-background-50/15 px-3 py-1.5 text-secondary-100 backdrop-blur-sm transition-colors hover:bg-primary-500 hover:text-background-50"
          >
            {categoryName(item.category)}
          </Link>
          {item.genres.map((g) => (
            <Link
              key={g}
              to={`/category/${genreLink(g)}`}
              className="rounded-md bg-background-50/15 px-3 py-1.5 text-secondary-100 backdrop-blur-sm transition-colors hover:bg-primary-500 hover:text-background-50"
            >
              {g}
            </Link>
          ))}
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 rounded-md bg-foreground-950/90 px-5 py-3 text-sm font-medium text-background-50 backdrop-blur-sm rf-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}